"use client";

import { useNextRouterContext } from "@faire/retailer-visitor-shared/app/_lib/routing/NextRouterContext";
import { getProductUrlWithSwatchOption } from "@faire/retailer-visitor-shared/features/MarketplaceItems/ProductTile/__internal__/components/utils/productUrlUtils";
import { usePrefetchOnHover } from "@faire/retailer-visitor-shared/hooks/pdpPrefetching/usePDPPrefetchOnHover";
import { usePrefetchOnTouch } from "@faire/retailer-visitor-shared/hooks/pdpPrefetching/usePDPPrefetchOnTouch";
import { useProductTileClickImpressions } from "@faire/retailer-visitor-shared/hooks/ProductTileImpression/useProductTileClickImpressions";
import { brandPage } from "@faire/retailer-visitor-shared/paths";
import { ProductPagePaths } from "@faire/retailer-visitor-shared/paths/ProductPage";
import { useSettingBrandLinkHoverThreshold } from "@faire/retailer-visitor-shared/settings/getSettingBrandLinkHoverTheshold";
import { usePreloadNavigationToPDP } from "@faire/retailer-visitor-shared/views/product/preload/preloadNavigationToPDP";
import { PrefetchKind } from "next/dist/client/components/router-reducer/router-reducer-types";
import {
  createContext,
  ReactNode,
  useCallback,
  useContext,
  useEffect,
  useState,
  useRef,
} from "react";

import {
  IBrandLinkProps,
  IProductLinkProps,
  ITileInteractions,
  ITrackingProps,
} from "../../types";

import { useSwatchInteraction } from "./Swatch/SwatchInteractionContextProvider";

export const TileInteractionsContextProvider = ({
  data,
  trackingProps,
  children,
}: {
  data?: ITileInteractions;
  trackingProps: ITrackingProps;
  children: ReactNode;
}) => {
  const { selectedSwatchOptionToken } = useSwatchInteraction();

  const productUrl = data?.props?.productLinkProps?.productUrl
    ? data?.props?.productLinkProps?.productUrl
    : ProductPagePaths.index(trackingProps.productToken ?? "", {
        impressionId: trackingProps.impressionId ?? undefined,
      });
  const [productPageLink, setProductPageLink] = useState<string>(productUrl);

  const getProductUrl = useCallback(
    (selectedToken?: string) => {
      return getProductUrlWithSwatchOption(
        trackingProps.productToken ?? "",
        productUrl ?? "",
        selectedToken
      );
    },
    [productUrl, trackingProps.productToken]
  );

  useEffect(() => {
    if (selectedSwatchOptionToken) {
      setProductPageLink(getProductUrl(selectedSwatchOptionToken));
    }
  }, [getProductUrl, selectedSwatchOptionToken]);

  // includes necessary impression tracking and preload functions
  const PREFETCH_DELAY_MS = useSettingBrandLinkHoverThreshold();
  const timeoutRef = useRef<ReturnType<typeof setTimeout> | undefined>(
    undefined
  );
  const { prefetch: nextPrefetch } = useNextRouterContext();

  const productLinkProps: IProductLinkProps = {
    productUrl: productPageLink,
  };

  const brandLinkProps: IBrandLinkProps = {
    brandUrl:
      data?.props?.brandLinkProps?.brandUrl ??
      brandPage(trackingProps.brandToken ?? "", {
        impressionId: trackingProps.impressionId,
      }),
    brandToken:
      data?.props?.brandLinkProps?.brandToken ?? trackingProps.brandToken ?? "",
    replace: data?.props?.brandLinkProps?.replace,
  };

  const preloadNavigationToPDP = usePreloadNavigationToPDP();

  const {
    recordBrandNameClickEvent,
    recordQuickAddSubmitEvent,
    recordQuickAddButtonClickEvent,
    recordChooseOptionClickEvent,
    recordChooseOptionQuantityUpdateSubmitEvent,
    recordProductTileClickEvent,
    recordHeartClickedEvent,
    recordSaveToBoardSubmitEvent,
    recordRemoveFromBoardSubmitEvent,
  } = useProductTileClickImpressions(
    {
      token: trackingProps.productToken,
      impressionId: trackingProps.impressionId,
      isPromoted: trackingProps.isPromoted,
      promotedBy: trackingProps.promotedBy,
    },
    trackingProps.brandToken
  );

  const preload = useCallback(() => {
    preloadNavigationToPDP(
      {
        showVerticalImagery: trackingProps.showVerticalImagery,
        productToken: trackingProps.productToken,
        bestImage: trackingProps.bestImage,
        images: trackingProps.images ?? [],
        brandToken: trackingProps.brandToken,
      },
      productPageLink
    );
  }, [
    preloadNavigationToPDP,
    trackingProps.productToken,
    trackingProps.bestImage,
    trackingProps.images,
    trackingProps.brandToken,
    trackingProps.showVerticalImagery,
    productPageLink,
  ]);

  const onProductClickBase = useCallback(() => {
    recordProductTileClickEvent();
    preload();
  }, [recordProductTileClickEvent, preload]);

  const onProductClickAction = useCallback(
    (productToken?: string) => {
      data?.tileActions?.onProductClick?.(productToken ?? "");
      onProductClickBase();
    },
    [onProductClickBase, data?.tileActions]
  );

  const onBrandClickAction = useCallback(() => {
    recordBrandNameClickEvent();
    data?.tileActions?.onBrandClick?.();
  }, [recordBrandNameClickEvent, data?.tileActions]);

  const onBrandLinkFocus = useCallback(() => {
    timeoutRef.current = setTimeout(() => {
      nextPrefetch(brandLinkProps.brandUrl, {
        kind: PrefetchKind.FULL,
      });
    }, PREFETCH_DELAY_MS);
  }, [brandLinkProps.brandUrl, nextPrefetch, PREFETCH_DELAY_MS]);
  const onBrandLinkBlur = useCallback(() => {
    clearTimeout(timeoutRef.current);
  }, []);

  const { onMouseEnter, onMouseLeave } = usePrefetchOnHover(
    {
      productToken: trackingProps.productToken,
      bestImage: trackingProps.bestImage,
      images: trackingProps.images ?? [],
      brandToken: trackingProps.brandToken,
    },
    productPageLink ?? ""
  );
  const { onTouchStart } = usePrefetchOnTouch(
    {
      productToken: trackingProps.productToken,
      bestImage: trackingProps.bestImage,
      images: trackingProps.images ?? [],
      brandToken: trackingProps.brandToken,
    },
    productPageLink ?? ""
  );

  const onQuickAddButtonClick = (newQuantity?: number) => {
    data?.overlayActions?.onQuickAddClick?.(newQuantity);
    recordQuickAddButtonClickEvent();
  };
  const onQuickAddSubmit = (newQuantity?: number) => {
    data?.overlayActions?.onQuickAddSubmit?.(newQuantity);
    recordQuickAddSubmitEvent(newQuantity);
  };

  const onChooseOptionClick = () => {
    data?.overlayActions?.onChooseOptionClick?.();
    recordChooseOptionClickEvent();
  };

  const onChooseOptionQuantityUpdateSubmit = (newQuantity?: number) => {
    data?.overlayActions?.onChooseOptionQuantityUpdateSubmit?.(newQuantity);
    recordChooseOptionQuantityUpdateSubmitEvent(newQuantity);
  };

  const onFavouriteClick = () => {
    data?.overlayActions?.onFavouriteClick?.();
    recordHeartClickedEvent();
  };

  return (
    <TileInteractionsContext.Provider
      value={{
        props: {
          productLinkProps: productLinkProps,
          brandLinkProps: data?.props?.brandLinkProps ?? brandLinkProps,
          productToken: trackingProps.productToken,
        },
        tileActions: {
          onProductClick: onProductClickAction,
          onBrandClick: onBrandClickAction,
          onMouseEnter: onMouseEnter,
          onMouseLeave: onMouseLeave,
          onMouseDown: preload,
          onTouchStart: onTouchStart,
          onBrandLinkFocus: onBrandLinkFocus,
          onBrandLinkBlur: onBrandLinkBlur,
        },
        overlayActions: {
          onFavouriteClick: onFavouriteClick,
          onSaveToBoardSubmit: recordSaveToBoardSubmitEvent,
          onRemoveFromBoardSubmit: recordRemoveFromBoardSubmitEvent,
          onQuickAddClick: onQuickAddButtonClick,
          onQuickAddSubmit: onQuickAddSubmit,
          onChooseOptionClick: onChooseOptionClick,
          onChooseOptionQuantityUpdateSubmit:
            onChooseOptionQuantityUpdateSubmit,
        },
      }}
    >
      {children}
    </TileInteractionsContext.Provider>
  );
};

const TileInteractionsContext = createContext<ITileInteractions | undefined>(
  undefined
);

export const useTileInteractions = () => {
  const context = useContext(TileInteractionsContext);
  if (!context) {
    throw new Error(
      "useTileInteractions must be used within a TileInteractionsContextProvider"
    );
  }
  return context;
};
